/**
 * MVC App View Service
 * Service layer for MAC (Maersk Application Catalog) information
 */

import { MVC_VIEW } from '../config/api';
import { authFetch } from './authFetch';

export interface MVCApplication {
  app_id: string;
  app_name: string;
  platform_portfolio: string;
  platform_name: string;
  portfolio_name: string;
  owners: string;
  owner_names: string;
  status: string;
  mvc_group: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface MVCApplicationExtended extends MVCApplication {
  business_outcome_priority: number | null;
  business_outcome_name: string | null;
  business_outcome_description: string | null;
  group_name: string | null;
  recovery_sequence: string | null;
  hosting: string | null;
  application_type: string | null;
  supports_gemini_network: string | null;
  admiral_product_code: string | null;
  start_date: string | null;
  end_date: string | null;
  reason: string | null;
  sr_fpo: string | null;
  gpl: string | null;
  sre_spoc: string | null;
}

export interface MVCApplicationsResponse {
  success: boolean;
  data: MVCApplication[];
  count: number;
  extended?: boolean;
  timestamp: string;
}

export interface MVCApplicationsExtendedResponse {
  success: boolean;
  data: MVCApplicationExtended[];
  count: number;
  extended: boolean;
  timestamp: string;
}

export interface MVCApplicationDetailResponse {
  success: boolean;
  data: MVCApplication;
  extended?: boolean;
  timestamp: string;
}

export interface MVCApplicationDetailExtendedResponse {
  success: boolean;
  data: MVCApplicationExtended;
  extended: boolean;
  timestamp: string;
}

export interface MVCStatistics {
  total_applications: number;
  total_portfolios: number;
  total_platforms: number;
  total_platform_portfolios: number;
  total_mvc_groups: number;
}

export interface MVCStatisticsResponse {
  success: boolean;
  data: MVCStatistics;
  timestamp: string;
}

export interface MVCListResponse {
  success: boolean;
  data: string[];
  count: number;
  timestamp: string;
}

/**
 * Fetch all MVC applications with optional filters
 */
export const fetchMVCApplications = async (
  portfolio?: string,
  platform?: string,
  appId?: string,
  extended?: boolean
): Promise<MVCApplication[]> => {
  try {
    const params = new URLSearchParams();
    if (portfolio && portfolio !== 'all') params.append('portfolio', portfolio);
    if (platform && platform !== 'all') params.append('platform', platform);
    if (appId) params.append('app_id', appId);
    if (extended) params.append('extended', 'true');

    const url = `${MVC_VIEW.APPLICATIONS}${params.toString() ? `?${params.toString()}` : ''}`;
    
    console.log('🔄 Fetching MVC applications:', url);
    
    const response = await authFetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: MVCApplicationsResponse = await response.json();
    
    if (!data.success) {
      throw new Error(data.timestamp || 'Failed to fetch MVC applications');
    }
    
    console.log('✅ MVC applications loaded:', data.count);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching MVC applications:', error);
    throw error;
  }
};

/**
 * Fetch all MVC applications with extended metadata
 */
export const fetchMVCApplicationsExtended = async (
  portfolio?: string,
  platform?: string,
  appId?: string
): Promise<MVCApplicationExtended[]> => {
  try {
    const params = new URLSearchParams();
    if (portfolio && portfolio !== 'all') params.append('portfolio', portfolio);
    if (platform && platform !== 'all') params.append('platform', platform);
    if (appId) params.append('app_id', appId);
    params.append('extended', 'true');

    const url = `${MVC_VIEW.APPLICATIONS}${params.toString() ? `?${params.toString()}` : ''}`;
    
    console.log('🔄 Fetching MVC applications (extended):', url);
    
    const response = await authFetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: MVCApplicationsExtendedResponse = await response.json();
    
    if (!data.success) {
      throw new Error(data.timestamp || 'Failed to fetch MVC applications');
    }
    
    console.log('✅ MVC applications (extended) loaded:', data.count);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching MVC applications (extended):', error);
    throw error;
  }
};

/**
 * Fetch details for a specific MVC application
 */
export const fetchMVCApplicationDetails = async (
  appId: string,
  extended?: boolean
): Promise<MVCApplication> => {
  try {
    const params = new URLSearchParams();
    if (extended) params.append('extended', 'true');
    
    const url = `${MVC_VIEW.APPLICATION(appId)}${params.toString() ? `?${params.toString()}` : ''}`;
    
    console.log('🔄 Fetching MVC application details:', url);
    
    const response = await authFetch(url);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Application not found');
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: MVCApplicationDetailResponse = await response.json();
    
    if (!data.success) {
      throw new Error(data.timestamp || 'Failed to fetch application details');
    }
    
    console.log('✅ MVC application details loaded:', data.data.app_name);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching MVC application details:', error);
    throw error;
  }
};

/**
 * Fetch details for a specific MVC application with extended metadata
 */
export const fetchMVCApplicationDetailsExtended = async (appId: string): Promise<MVCApplicationExtended> => {
  try {
    const url = `${MVC_VIEW.APPLICATION(appId)}?extended=true`;
    
    console.log('🔄 Fetching MVC application details (extended):', url);
    
    const response = await authFetch(url);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Application not found');
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: MVCApplicationDetailExtendedResponse = await response.json();
    
    if (!data.success) {
      throw new Error(data.timestamp || 'Failed to fetch application details');
    }
    
    console.log('✅ MVC application details (extended) loaded:', data.data.app_name);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching MVC application details (extended):', error);
    throw error;
  }
};

/**
 * Fetch list of unique portfolios
 */
export const fetchMVCPortfolios = async (): Promise<string[]> => {
  try {
    const url = MVC_VIEW.PORTFOLIOS;
    
    console.log('🔄 Fetching MVC portfolios:', url);
    
    const response = await authFetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: MVCListResponse = await response.json();
    
    if (!data.success) {
      throw new Error(data.timestamp || 'Failed to fetch portfolios');
    }
    
    console.log('✅ MVC portfolios loaded:', data.count);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching MVC portfolios:', error);
    throw error;
  }
};

/**
 * Fetch list of unique platforms, optionally filtered by portfolio
 */
export const fetchMVCPlatforms = async (portfolio?: string): Promise<string[]> => {
  try {
    const params = new URLSearchParams();
    if (portfolio && portfolio !== 'all') params.append('portfolio', portfolio);
    
    const url = `${MVC_VIEW.PLATFORMS}${params.toString() ? `?${params.toString()}` : ''}`;
    
    console.log('🔄 Fetching MVC platforms:', url);
    
    const response = await authFetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: MVCListResponse = await response.json();
    
    if (!data.success) {
      throw new Error(data.timestamp || 'Failed to fetch platforms');
    }
    
    console.log('✅ MVC platforms loaded:', data.count);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching MVC platforms:', error);
    throw error;
  }
};

/**
 * Fetch list of unique platform portfolios
 */
export const fetchMVCPlatformPortfolios = async (): Promise<string[]> => {
  try {
    const url = MVC_VIEW.PLATFORM_PORTFOLIOS;
    
    console.log('🔄 Fetching MVC platform portfolios:', url);
    
    const response = await authFetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: MVCListResponse = await response.json();
    
    if (!data.success) {
      throw new Error(data.timestamp || 'Failed to fetch platform portfolios');
    }
    
    console.log('✅ MVC platform portfolios loaded:', data.count);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching MVC platform portfolios:', error);
    throw error;
  }
};

/**
 * Fetch overall MVC statistics
 */
export const fetchMVCStatistics = async (): Promise<MVCStatistics> => {
  try {
    const url = MVC_VIEW.STATISTICS;
    
    console.log('🔄 Fetching MVC statistics:', url);
    
    const response = await authFetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: MVCStatisticsResponse = await response.json();
    
    if (!data.success) {
      throw new Error(data.timestamp || 'Failed to fetch statistics');
    }
    
    console.log('✅ MVC statistics loaded:', data.data);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching MVC statistics:', error);
    throw error;
  }
};

/**
 * Update extended metadata for an MVC application
 */
export const updateMVCApplicationMetadata = async (
  appId: string,
  metadata: {
    recovery_sequence?: string;
    hosting?: string;
    application_type?: string;
    supports_gemini_network?: string;
    admiral_product_code?: string;
    start_date?: string;
    end_date?: string;
    reason?: string;
    sr_fpo?: string;
    gpl?: string;
    sre_spoc?: string;
  }
): Promise<MVCApplicationExtended> => {
  try {
    const url = `${MVC_VIEW.APPLICATION(appId)}/metadata`;
    
    console.log('🔄 Updating MVC application metadata:', appId);
    
    const response = await authFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(metadata),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data: MVCApplicationDetailExtendedResponse = await response.json();
    
    if (!data.success) {
      throw new Error('Failed to update application metadata');
    }
    
    console.log('✅ MVC application metadata updated:', appId);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error updating MVC application metadata:', error);
    throw error;
  }
};

/**
 * Fetch audit history for an MVC application
 */
export const fetchMVCApplicationAuditHistory = async (appId: string): Promise<any> => {
  try {
    const url = `${MVC_VIEW.APPLICATION(appId)}/mvc-view-audit-history`;
    
    console.log('🔄 Fetching MVC application audit history:', appId);
    
    const response = await authFetch(url);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error('Failed to fetch audit history');
    }
    
    console.log('✅ MVC application audit history fetched:', appId);
    return data.data;
    
  } catch (error) {
    console.error('❌ Error fetching MVC application audit history:', error);
    throw error;
  }
};
